import { useCallback, useEffect, useState } from 'react'
import {
  Box,
  Typography,
  CircularProgress,
  Chip,
  Button,
  IconButton,
  Divider,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import BlockIcon from '@mui/icons-material/Block'
import SendIcon from '@mui/icons-material/Send'
import { toast } from 'react-toastify'
import {
  fetchCertificatesForPatient,
  revokeCertificate,
  sendCertificateToPatient,
} from '../medicalCertificateService'
import { CERTIFICATE_STATUS, formatDisplayDate } from '../certificateUtils'

const STATUS_COLOR = {
  [CERTIFICATE_STATUS.ISSUED]: 'success',
  [CERTIFICATE_STATUS.DRAFT]: 'default',
  [CERTIFICATE_STATUS.REVOKED]: 'error',
}

/** View mode: lists a patient's certificates with open / revoke. */
export default function ExistingCertificatesList({ patientId, onCreateNew }) {
  const [loading, setLoading] = useState(true)
  const [certificates, setCertificates] = useState([])
  const [error, setError] = useState(null)
  const [revokingId, setRevokingId] = useState(null)
  // { open, cert, state: null|'queued'|'sent', sending }
  const [sendDialog, setSendDialog] = useState({ open: false, cert: null, state: null, sending: false })

  const openSendForCert = async (cert) => {
    setSendDialog({ open: true, cert, state: null, sending: true })
    const res = await sendCertificateToPatient(cert.id, false)
    if (res.ok) {
      setSendDialog({ open: true, cert, state: 'queued', sending: false })
    } else {
      setSendDialog({ open: false, cert: null, state: null, sending: false })
      toast.error(res.message || 'Failed to queue certificate')
    }
  }

  const handleSendNow = async () => {
    const cert = sendDialog.cert
    if (!cert) return
    setSendDialog((s) => ({ ...s, sending: true }))
    const res = await sendCertificateToPatient(cert.id, true)
    if (res.ok) {
      setSendDialog((s) => ({ ...s, state: 'sent', sending: false }))
      toast.success('Certificate sent to the patient')
    } else {
      setSendDialog((s) => ({ ...s, sending: false }))
      toast.error(res.message || 'Failed to send now')
    }
  }

  const closeSendDialog = () => setSendDialog({ open: false, cert: null, state: null, sending: false })

  const load = useCallback(() => {
    if (!patientId) {
      setLoading(false)
      return
    }
    setLoading(true)
    fetchCertificatesForPatient(patientId)
      .then((res) => {
        if (res.ok) setCertificates(res.certificates)
        else setError(res.message)
      })
      .finally(() => setLoading(false))
  }, [patientId])

  useEffect(() => {
    load()
  }, [load])

  const handleOpen = (cert) => {
    if (cert?.pdfUrl) window.open(cert.pdfUrl, '_blank', 'noopener')
    else toast.error('PDF link not available')
  }

  const handleRevoke = async (cert) => {
    const reason = window.prompt('Reason for revoking this certificate?')
    if (reason === null) return
    setRevokingId(cert.id)
    const res = await revokeCertificate(cert.id, reason)
    setRevokingId(null)
    if (res.ok) {
      toast.success('Certificate revoked')
      load()
    } else {
      toast.error(res.message || 'Failed to revoke')
    }
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress size={28} />
      </Box>
    )
  }

  if (error) {
    return (
      <Box sx={{ py: 6, textAlign: 'center' }}>
        <Typography color="error">{error}</Typography>
      </Box>
    )
  }

  if (!certificates.length) {
    return (
      <Box sx={{ py: 6, textAlign: 'center' }}>
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          No certificates have been issued for this patient yet.
        </Typography>
        {onCreateNew && (
          <Button variant="contained" onClick={onCreateNew} sx={{ textTransform: 'none' }}>
            Create a certificate
          </Button>
        )}
      </Box>
    )
  }

  return (
    <>
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      {certificates.map((cert, idx) => (
        <Box key={cert.id}>
          {idx > 0 && <Divider />}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.5, gap: 2 }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 600, fontSize: 14 }} noWrap>
                {cert.certificateTitle || 'Medical document'}
              </Typography>
              <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                {formatDisplayDate(cert.issuedAt || cert.createdTime)}
                {cert.practitionerName ? ` • ${cert.practitionerName}` : ''}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
              <Chip
                size="small"
                label={cert.status || CERTIFICATE_STATUS.ISSUED}
                color={STATUS_COLOR[cert.status] || 'default'}
                variant="outlined"
              />
              <Tooltip title="Open PDF">
                <IconButton size="small" onClick={() => handleOpen(cert)}>
                  <OpenInNewIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              {cert.status !== CERTIFICATE_STATUS.REVOKED && (
                <Tooltip title="Send to patient">
                  <IconButton size="small" color="secondary" onClick={() => openSendForCert(cert)}>
                    <SendIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {cert.status !== CERTIFICATE_STATUS.REVOKED && (
                <Tooltip title="Revoke">
                  <span>
                    <IconButton size="small" color="error" onClick={() => handleRevoke(cert)} disabled={revokingId === cert.id}>
                      <BlockIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
            </Box>
          </Box>
        </Box>
      ))}
    </Box>

      <Dialog open={sendDialog.open} onClose={closeSendDialog} maxWidth="xs" fullWidth>
        <DialogTitle>{sendDialog.state === 'sent' ? 'Sent to patient' : 'Queued for the patient'}</DialogTitle>
        <DialogContent>
          {sendDialog.sending && sendDialog.state === null ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={22} />
            </Box>
          ) : (
            <DialogContentText sx={{ fontSize: 14 }}>
              {sendDialog.state === 'sent'
                ? 'The certificate has been sent to the patient on their registered contact (email / WhatsApp).'
                : 'The certificate will be sent to the patient on their registered contact (email / WhatsApp) — usually within ~15 minutes during the day, or ~40 minutes at night. You can send it right away instead.'}
            </DialogContentText>
          )}
        </DialogContent>
        <DialogActions>
          {sendDialog.state === 'queued' && (
            <Button onClick={handleSendNow} disabled={sendDialog.sending} startIcon={<SendIcon />} sx={{ textTransform: 'none' }}>
              Send now
            </Button>
          )}
          <Button variant="contained" onClick={closeSendDialog} sx={{ textTransform: 'none' }}>
            {sendDialog.state === 'sent' ? 'Done' : 'OK'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
